import AddRounded from "@mui/icons-material/AddRounded";
import PlaylistAddRounded from "@mui/icons-material/PlaylistAddRounded";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useMyFacilityActions } from "@/hooks/use-my-facility-actions";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { useMyOrg } from "@/hooks/use-my-org";

import {
  createCourt,
  deleteCourt,
  listCourts,
  updateCourt,
  type Court,
  type CourtStatus,
} from "@/libs/facilityApi";
import { BulkCourtDialog } from "./bulk-court-dialog";
import { CourtInventoryPanels } from "./court-inventory-panels";

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; court: Court };

interface FacilityScope {
  facilityId: string | null;
}

function sortCourts(courts: Court[]): Court[] {
  return courts.sort((left, right) =>
    left.name.localeCompare(right.name, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

export function CourtsWorkspace() {
  const { org } = useMyOrg();
  const preparing = org?.status === "pending" || org?.status === "in_review";
  // The active facility comes from the FacilityContext selection (resolved
  // against the org's facility list) — the Layer-3 gate guarantees a facility is
  // selected before this workspace renders, so we only handle the brief resolve
  // window, never a "no facility" error.
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const { actions, isLoading: actionsLoading } =
    useMyFacilityActions(facilityId);
  const canManageCourts = !actionsLoading && actions.includes("facility:court");

  const [rows, setRows] = useState<Court[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const [pendingDelete, setPendingDelete] = useState<Court | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // form state
  const [name, setName] = useState("");
  const [status, setStatus] = useState<CourtStatus>("enabled");
  const [tags, setTags] = useState("");
  const [maxPlayers, setMaxPlayers] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const facilityScopeRef = useRef<FacilityScope>({ facilityId });
  const rowsScopeRef = useRef<FacilityScope | null>(null);
  const latestListRequest = useRef(0);
  if (facilityScopeRef.current.facilityId !== facilityId) {
    facilityScopeRef.current = { facilityId };
  }
  const renderedFacilityScope = facilityScopeRef.current;
  const visibleRows =
    rowsScopeRef.current === renderedFacilityScope ? rows : [];
  const inventoryLoading =
    loading || rowsScopeRef.current !== renderedFacilityScope;

  const isCurrentScope = useCallback(
    (scope: FacilityScope) => facilityScopeRef.current === scope,
    [],
  );

  const refresh = useCallback(async (scope: FacilityScope) => {
    if (!scope.facilityId) return false;
    const requestNumber = ++latestListRequest.current;
    const courts = await listCourts(scope.facilityId);
    if (
      facilityScopeRef.current !== scope ||
      latestListRequest.current !== requestNumber
    ) {
      return false;
    }
    rowsScopeRef.current = scope;
    setRows(courts);
    return true;
  }, []);

  useEffect(() => {
    const scope = facilityScopeRef.current;
    rowsScopeRef.current = scope;
    setRows([]);
    setError(null);
    setDialog({ mode: "closed" });
    setPendingDelete(null);
    setBulkOpen(false);
    setFormError(null);
    setSaving(false);
    setDeleting(false);
    if (!scope.facilityId) return;
    setLoading(true);
    (async () => {
      try {
        await refresh(scope);
      } catch (err) {
        if (isCurrentScope(scope)) {
          setError(
            err instanceof Error ? err.message : "Failed to load courts",
          );
        }
      } finally {
        if (isCurrentScope(scope)) setLoading(false);
      }
    })();
  }, [facilityId, isCurrentScope, refresh]);

  useEffect(() => {
    if (canManageCourts) return;
    setDialog({ mode: "closed" });
    setPendingDelete(null);
    setBulkOpen(false);
  }, [canManageCourts]);

  const openCreate = () => {
    setName("");
    setStatus("enabled");
    setTags("");
    setMaxPlayers("");
    setFormError(null);
    setDialog({ mode: "create" });
  };

  const openEdit = (court: Court) => {
    setName(court.name);
    setStatus(court.status === "deleted" ? "disabled" : court.status);
    setTags((court.tags ?? []).join(", "));
    setMaxPlayers(court.max_players?.toString() ?? "");
    setFormError(null);
    setDialog({ mode: "edit", court });
  };

  const submit = async () => {
    const scope = facilityScopeRef.current;
    if (!scope.facilityId) return;
    if (!name.trim()) {
      setFormError("Name is required.");
      return;
    }
    const tagList = tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const parsedMaxPlayers =
      maxPlayers.trim() === "" ? null : Number(maxPlayers);
    if (
      parsedMaxPlayers !== null &&
      (!Number.isInteger(parsedMaxPlayers) ||
        parsedMaxPlayers < 1 ||
        parsedMaxPlayers > 50)
    ) {
      setFormError("Capacity must be a whole number from 1 to 50.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      let savedCourt: Court;
      if (dialog.mode === "edit") {
        savedCourt = await updateCourt(dialog.court.court_id, {
          name,
          status,
          tags: tagList,
          max_players: parsedMaxPlayers,
        });
      } else {
        savedCourt = await createCourt(scope.facilityId, {
          name,
          status,
          tags: tagList,
          ...(parsedMaxPlayers === null
            ? {}
            : { max_players: parsedMaxPlayers }),
        });
      }
      if (!isCurrentScope(scope)) return;
      setRows((current) =>
        sortCourts([
          ...current.filter((court) => court.court_id !== savedCourt.court_id),
          savedCourt,
        ]),
      );
      setDialog({ mode: "closed" });
      try {
        await refresh(scope);
      } catch {
        if (isCurrentScope(scope)) {
          setError(
            "The court was saved, but the inventory could not refresh. Reload to reconcile the list.",
          );
        }
      }
    } catch (err) {
      if (isCurrentScope(scope)) {
        setFormError(err instanceof Error ? err.message : "Save failed");
      }
    } finally {
      if (isCurrentScope(scope)) setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const scope = facilityScopeRef.current;
    const court = pendingDelete;
    if (!court || !scope.facilityId) return;
    setDeleting(true);
    try {
      await deleteCourt(court.court_id);
      if (!isCurrentScope(scope)) return;
      setPendingDelete(null);
      setRows((current) =>
        current.filter((row) => row.court_id !== court.court_id),
      );
      try {
        await refresh(scope);
      } catch {
        if (isCurrentScope(scope)) {
          setError(
            "The court was retired, but the inventory could not refresh. Reload to reconcile the list.",
          );
        }
      }
    } catch (err) {
      if (isCurrentScope(scope)) {
        setError(err instanceof Error ? err.message : "Delete failed");
      }
    } finally {
      if (isCurrentScope(scope)) setDeleting(false);
    }
  };

  // Brief resolve window: the FacilityContext selection hasn't landed yet. Show
  // a loading affordance rather than an empty table or a "no facility" error
  // (the Layer-3 gate owns the genuine no-facility case).
  if (facilityLoading || !facilityId) {
    return (
      <Stack
        aria-live="polite"
        direction="row"
        role="status"
        spacing={1}
        sx={{
          minHeight: "40vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      {preparing ? (
        <Alert severity="info">
          <AlertTitle>Private court setup</AlertTitle>
          Prepare your court inventory before your business is approved.
          Adding courts does not publish your facility or enable bookings.
          <Box sx={{ mt: 1 }}>
            <Button component={Link} to="/getting-started" size="small">
              Back to business setup
            </Button>
          </Box>
        </Alert>
      ) : null}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
            Courts
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
            {facility?.name ?? "Court inventory and status."}
          </Typography>
        </Box>
        {canManageCourts ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", rowGap: 1 }}
          >
            <Button
              aria-label="Add courts in bulk"
              disabled={inventoryLoading}
              onClick={() => setBulkOpen(true)}
              size="small"
              startIcon={<PlaylistAddRounded />}
              variant="outlined"
            >
              Bulk add
            </Button>
            <Button
              disabled={inventoryLoading}
              onClick={openCreate}
              size="small"
              startIcon={<AddRounded />}
              variant="contained"
            >
              New court
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <CourtInventoryPanels
        courts={visibleRows}
        loading={inventoryLoading}
        canManageCourts={canManageCourts}
        onEdit={openEdit}
        onRetire={setPendingDelete}
      />

      <Dialog
        aria-describedby="court-form-description"
        fullWidth
        maxWidth="sm"
        open={dialog.mode !== "closed"}
        onClose={() => !saving && setDialog({ mode: "closed" })}
      >
        <DialogTitle>
          {dialog.mode === "edit" ? "Edit court" : "New court"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText id="court-form-description">
              Courts are bookable units at this facility.
            </DialogContentText>
            {formError ? <Alert severity="error">{formError}</Alert> : null}
            <TextField
              autoFocus
              fullWidth
              id="c-name"
              label="Name"
              onChange={(event) => setName(event.target.value)}
              size="small"
              value={name}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="c-status-label">Status</InputLabel>
              <Select
                label="Status"
                labelId="c-status-label"
                onChange={(event) =>
                  setStatus(event.target.value as CourtStatus)
                }
                value={status}
              >
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              id="c-tags"
              label="Tags"
              onChange={(event) => setTags(event.target.value)}
              placeholder="comma, separated"
              size="small"
              value={tags}
            />
            <TextField
              fullWidth
              id="c-capacity"
              label="Capacity (optional)"
              onChange={(event) => setMaxPlayers(event.target.value)}
              size="small"
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
              value={maxPlayers}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={saving}
            onClick={() => setDialog({ mode: "closed" })}
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            disabled={saving}
            onClick={submit}
            size="small"
            variant="contained"
          >
            {saving
              ? "Saving…"
              : dialog.mode === "edit"
                ? "Save changes"
                : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-describedby="retire-court-description"
        fullWidth
        maxWidth="xs"
        open={Boolean(pendingDelete)}
        onClose={() => !deleting && setPendingDelete(null)}
      >
        <DialogTitle>Retire court?</DialogTitle>
        <DialogContent>
          <DialogContentText id="retire-court-description">
            This removes <strong>{pendingDelete?.name}</strong> from active
            inventory and safely reconciles future use.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleting}
            onClick={() => setPendingDelete(null)}
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deleting}
            onClick={confirmDelete}
            size="small"
            variant="contained"
          >
            {deleting ? "Retiring…" : "Retire court"}
          </Button>
        </DialogActions>
      </Dialog>

      {canManageCourts ? (
        <BulkCourtDialog
          open={bulkOpen}
          facilityId={facilityId}
          onOpenChange={setBulkOpen}
          onCommitted={async (createdCourts) => {
            if (!isCurrentScope(renderedFacilityScope)) return;
            setRows((current) => {
              const merged = new Map(
                current.map((court) => [court.court_id, court]),
              );
              createdCourts.forEach((court) =>
                merged.set(court.court_id, court),
              );
              return sortCourts([...merged.values()]);
            });
            try {
              await refresh(renderedFacilityScope);
            } catch {
              if (isCurrentScope(renderedFacilityScope)) {
                setError(
                  "The courts were created, but the inventory could not refresh. Reload to reconcile the list.",
                );
              }
            }
          }}
        />
      ) : null}
    </Stack>
  );
}
